/**
 * @file  services/audit/index.ts
 * @description  audit service to track telemetry digest information
 *
 * @author ogarcia (ozkary)
 *
 */

import * as _cache from '../../services/cache';
import * as _date from '../../services/datetime';
import { Audit, AuditStatus } from '../../functions/audit/audit.model';
import { getCustomer } from '../customer';
import { getVehicle } from '../Vehicle';
import { error } from '../logger';
import { saveAudit, submitAudit } from '../../functions/audit';

/**
 * gets the audit information from the cache
 * @returns the audit information
 */
export const getAudit = async (): Promise<Audit> => {
  return _cache.getItem<Audit>(_cache.Key.audit);
};

/**
 *  initialize an audit session
 */
export const initializeAudit = (): Audit => {
  return {
    status: AuditStatus.visit,
    pageView: [location.pathname],
    id: 0, // zero means tp track that it has not been updated in the backend
    created: _date.isoDate(),
    domain: location.hostname,
    edition: process.env.REACT_APP_EDITION ?? '',
    token: new URLSearchParams(window.location.search).get('token') || ''
  };
};

/**
 * tracks the audit information like customer and vehicle, so it
 * can be shared with external services
 */
export const track = async (
  audit: Audit | undefined
): Promise<Audit | undefined> => {
  if (!audit) {
    audit = { ...initializeAudit() };
  }
  // get the customer and vehicle data.
  const customer = await getCustomer();
  const car = await getVehicle();

  try {
    // cancel track when have a token and doesn't have customer/car. track will be called after get contract details
    if (audit?.token && (customer === undefined || car === undefined)) {
      return undefined;
    }

    // compose a doc with a copy of the audit to avoid caching that information into the audit entry
    const digest = {
      ...audit,
      customer: { ...customer },
      vehicle: { ...car },
      updated: _date.isoDate()
    };

    if (audit?.token || customer || car) {
      // wait for the function call to save the data and get the id back
      const result = await saveAudit(digest);
      if (audit?.id === 0) {
        digest.id = result.id;
      }
      // update the cache with the id
      _cache.setItem<Audit>(_cache.Key.audit, result);
      // fire the digest call
      submitAudit(digest);

      return result;
    } else {
      _cache.setItem<Audit>(_cache.Key.audit, digest);
      return digest;
    }
  } catch (err: unknown) {
    error(err as Error);
  }
};
